import React, { useEffect, useState } from "react";
import axios from "axios";
import { useApp } from "../store";
import { FileText, Upload, Plus, Trash2, ExternalLink, X } from "lucide-react";

export default function LotsManager({ productId, productName, onClose }) {
  const { API, authHeaders } = useApp();
  const [lots, setLots] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    axios.get(`${API}/products/${productId}/lots`).then(r => setLots(r.data));
  };
  useEffect(load, [API, productId]);

  const newLot = () => setEditing({
    lot_number: "",
    batch_date: new Date().toISOString().slice(0, 10),
    coa_url: "", gcms_url: "", status: "active", notes: "",
  });

  const save = async () => {
    setBusy(true);
    try {
      if (editing.id) {
        await axios.put(`${API}/lots/${editing.id}`, editing, { headers: authHeaders });
      } else {
        await axios.post(`${API}/products/${productId}/lots`, editing, { headers: authHeaders });
      }
      setEditing(null); load();
    } finally { setBusy(false); }
  };

  const removeLot = async (id) => {
    if (!window.confirm("Delete this lot?")) return;
    await axios.delete(`${API}/lots/${id}`, { headers: authHeaders });
    load();
  };

  const uploadDoc = async (field, file) => {
    const fd = new FormData(); fd.append("file", file);
    const r = await axios.post(`${API}/upload`, fd, { headers: { ...authHeaders, "Content-Type": "multipart/form-data" } });
    setEditing(e => ({ ...e, [field]: `${process.env.REACT_APP_BACKEND_URL}${r.data.url}` }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[110] flex items-center justify-center p-4" data-testid="lots-manager">
      <div className="bg-[#FAF9F6] w-full max-w-4xl max-h-[90vh] overflow-y-auto p-8">
        <div className="flex justify-between items-start mb-6">
          <div>
            <div className="overline text-copper mb-1">Lot Management</div>
            <h3 className="font-serif-display text-3xl">{productName}</h3>
          </div>
          <button onClick={onClose} data-testid="lots-close"><X size={22} /></button>
        </div>

        {!editing && (
          <>
            <button onClick={newLot} className="btn-primary mb-6" data-testid="add-lot">
              <Plus size={14} /> Add new lot
            </button>
            {lots.length === 0 ? (
              <p className="text-[#8A8D8A] italic font-serif-display text-xl">No lots added yet.</p>
            ) : (
              <div className="divide-y divide-[#E5E1D8] border-y border-[#E5E1D8]">
                {lots.map(l => (
                  <div key={l.id} className="grid grid-cols-12 gap-4 py-4 items-center" data-testid={`lot-row-${l.id}`}>
                    <div className="col-span-3">
                      <div className="font-mono text-sm">{l.lot_number}</div>
                      <div className="text-xs text-[#8A8D8A]">{l.batch_date}</div>
                    </div>
                    <div className="col-span-2">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${l.status === "active" ? "bg-[#3C6348]/10 text-[#3C6348]" : "bg-[#EBE4D8] text-[#5C5E5C]"}`}>{l.status}</span>
                    </div>
                    <div className="col-span-3 text-xs">
                      {l.coa_url ? <a href={l.coa_url} target="_blank" rel="noreferrer" className="text-copper underline flex items-center gap-1"><FileText size={12} /> COA</a> : <span className="text-[#8A8D8A]">No COA</span>}
                    </div>
                    <div className="col-span-2 text-xs">
                      {l.gcms_url ? <a href={l.gcms_url} target="_blank" rel="noreferrer" className="text-copper underline flex items-center gap-1"><FileText size={12} /> GC/MS</a> : <span className="text-[#8A8D8A]">No GC/MS</span>}
                    </div>
                    <div className="col-span-2 flex gap-3 justify-end">
                      <button onClick={() => setEditing(l)} className="overline text-[10px] text-copper hover:underline" data-testid={`edit-lot-${l.id}`}>Edit</button>
                      <button onClick={() => removeLot(l.id)} className="text-[#9E3C3C]"><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {editing && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Lot Number" value={editing.lot_number} onChange={v => setEditing({ ...editing, lot_number: v })} />
              <Field label="Batch Date" type="date" value={editing.batch_date} onChange={v => setEditing({ ...editing, batch_date: v })} />
              <div>
                <label className="overline text-[10px] block mb-2">Status</label>
                <select value={editing.status} onChange={e => setEditing({ ...editing, status: e.target.value })} className="input-underline">
                  <option value="active">Active</option>
                  <option value="sold_out">Sold out</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>
            <DocField label="Certificate of Analysis (COA)" url={editing.coa_url}
              onUrlChange={v => setEditing({ ...editing, coa_url: v })}
              onFile={f => uploadDoc("coa_url", f)} />
            <DocField label="GC/MS Report" url={editing.gcms_url}
              onUrlChange={v => setEditing({ ...editing, gcms_url: v })}
              onFile={f => uploadDoc("gcms_url", f)} />
            <div>
              <label className="overline text-[10px] block mb-2">Lot Notes</label>
              <textarea value={editing.notes || ""} onChange={e => setEditing({ ...editing, notes: e.target.value })} className="input-underline" rows={3} />
            </div>
            <div className="flex gap-3">
              <button onClick={save} disabled={busy} className="btn-primary" data-testid="save-lot">{busy ? "Saving…" : "Save lot"}</button>
              <button onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="overline text-[10px] block mb-2">{label}</label>
      <input type={type} value={value || ""} onChange={e => onChange(e.target.value)} className="input-underline" />
    </div>
  );
}

function DocField({ label, url, onUrlChange, onFile }) {
  return (
    <div>
      <label className="overline text-[10px] block mb-2">{label}</label>
      <div className="grid md:grid-cols-2 gap-3">
        <input type="url" placeholder="Paste public PDF URL (Drive, Dropbox, S3...)" value={url || ""} onChange={e => onUrlChange(e.target.value)} className="input-underline" />
        <label className="border border-dashed border-[#E5E1D8] hover:border-copper px-4 py-3 text-xs text-center cursor-pointer flex items-center justify-center gap-2 transition-colors">
          <Upload size={14} /> Or upload PDF / image
          <input type="file" accept="application/pdf,image/*" onChange={e => e.target.files?.[0] && onFile(e.target.files[0])} className="hidden" />
        </label>
      </div>
      {url && <a href={url} target="_blank" rel="noreferrer" className="text-xs text-copper underline mt-2 inline-flex items-center gap-1"><ExternalLink size={12} /> Preview</a>}
    </div>
  );
}
